import { zodResolver } from "@hookform/resolvers/zod";
import type { JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import { toast } from "@/shared/lib/toast";
import { Modal } from "@/shared/ui/modal";

import { useCreateCampaign } from "../api/use-create-campaign";
import { useRenameCampaign } from "../api/use-rename-campaign";
import { campaignSchema, type CampaignFormValues } from "../lib/campaign-schema";

import { PrimaryButton, SecondaryButton } from "./buttons";

interface CampaignFormModalProps {
  /** When provided the modal renames an existing campaign; otherwise it creates. */
  campaign?: { id: string; name: string };
  onClose: () => void;
}

const FIELD_CLASS =
  "h-10 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring disabled:opacity-50";

export function CampaignFormModal({ campaign, onClose }: CampaignFormModalProps): JSX.Element {
  const { t } = useTranslation("campaign");
  const isRename = Boolean(campaign);
  const create = useCreateCampaign();
  const rename = useRenameCampaign();
  const isPending = create.isPending || rename.isPending;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignSchema),
    defaultValues: { name: campaign?.name ?? "" },
  });

  function onSubmit(values: CampaignFormValues): void {
    const onError = (err: unknown): void => toast.error(apiErrorMessage(err));
    if (campaign) {
      rename.mutate({ id: campaign.id, body: values }, { onSuccess: () => onClose(), onError });
    } else {
      create.mutate(values, { onSuccess: () => onClose(), onError });
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isRename ? t("form.rename_title") : t("form.create_title")}
      footer={
        <>
          <SecondaryButton
            type="button"
            onClick={onClose}
            disabled={isPending}
            data-testid="campaign-form-cancel"
          >
            {t("form.cancel")}
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            form="campaign-form"
            disabled={isPending}
            data-testid="campaign-form-submit"
          >
            {isRename ? t("form.submit_rename") : t("form.submit_create")}
          </PrimaryButton>
        </>
      }
    >
      <form
        id="campaign-form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        data-testid="campaign-form"
      >
        <label
          htmlFor="campaign-name"
          className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
        >
          {t("form.name_label")}
        </label>
        <input
          id="campaign-name"
          type="text"
          data-testid="campaign-name-input"
          placeholder={t("form.name_placeholder")}
          disabled={isPending}
          className={FIELD_CLASS}
          {...register("name")}
        />
        {errors.name && (
          <p
            className="mt-1 text-xs text-[var(--intent-danger-fg)]"
            data-testid="campaign-name-error"
          >
            {t(errors.name.message ?? "form.err_name_required")}
          </p>
        )}
      </form>
    </Modal>
  );
}
